import React from 'react';
import { Typography } from '@pwskills/rachnaui';
import { IInstructorDetails, ISocialLinks } from '@/interfaces/courseDescription';
import {
  Youtube,
  PeopleOutline,
  BriefcaseSharp,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
} from '@pwskills/rachnaui/Icons';
import Link from 'next/link';
import { companyImage } from '@/utils/common/env.constants';

interface Props {
  instructorDetails: IInstructorDetails;
  name: string;
  description: string;
  social: ISocialLinks;
  isLinkedInButtonEnabled: boolean;
  isSocialIconEnabled: boolean;
  learnersPlacedToBeShown: boolean;
  industryExpToBeShown: boolean;
}

const getSocialUrl = (value?: string | { url?: string }) =>
  typeof value === 'string' ? value : value?.url;

const CardDetailsView = (props: Props) => {
  const {
    instructorDetails,
    name,
    description,
    social,
    isLinkedInButtonEnabled,
    isSocialIconEnabled,
    learnersPlacedToBeShown,
    industryExpToBeShown,
  } = props;
  const {
    designation,
    workExperience,
    teachingExperience,
    industryExp,
    learnersPlaced,
    pastCompanies,
  } = instructorDetails || {};

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-row justify-between items-start">
            <div className="w-[85%]">
              <Typography
                component="h4"
                variant="bold"
                className="break-words text-gray-900 !text-xl line-clamp-1"
              >
                {name}
              </Typography>
              <Typography
                component="body-tiny"
                variant="regular"
                className="break-words text-gray-650 mb-2 !text-xs line-clamp-1"
              >
                {designation}
              </Typography>
              {isSocialIconEnabled && (
                <div className="flex gap-2">
                  {Object.entries({
                    linkedin: {
                      url: getSocialUrl(social?.linkedin),
                      Component: Linkedin,
                      hoverColor: 'hover:text-[#0177B5]',
                    },
                    youtube: {
                      url: getSocialUrl(social?.youtube),
                      Component: Youtube,
                      hoverColor: 'hover:text-[#FF0002]',
                    },
                    facebook: {
                      url: getSocialUrl(social?.facebook),
                      Component: Facebook,
                      hoverColor: 'hover:text-[#4867AA]',
                    },
                    instagram: {
                      url: getSocialUrl(social?.instagram),
                      Component: Instagram,
                      hoverColor: 'hover:text-[#B806E4]',
                    },
                    twitter: {
                      url: getSocialUrl(social?.twitter),
                      Component: Twitter,
                      hoverColor: 'hover:text-[#1DA1F1]',
                    },
                  }).map(
                    ([key, { url, Component, hoverColor }]) =>
                      url && (
                        <a key={key} href={url} target="_blank" rel="noreferrer noopener">
                          <Component className={`text-gray-600 w-5 h-5 ${hoverColor}`} />
                        </a>
                      )
                  )}
                </div>
              )}
            </div>

            {isLinkedInButtonEnabled && getSocialUrl(social?.linkedin) && (
              <div className="flex items-center justify-center">
                <Link href={getSocialUrl(social?.linkedin) || ''} target="_blank">
                  <img
                    src="/images/homePageRevamp/LinkedinIcon.svg"
                    height={24}
                    width={24}
                    alt="linkedin"
                  />
                </Link>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {pastCompanies?.slice(0, 3)?.map((company) => {
              return (
                <img
                  key={company?._id}
                  src={`${companyImage}/company_logos/${
                    company?.companyLogoFileName
                  }?timestamp=${Date.now()}`}
                  alt="company logo"
                  className="w-[100px] h-[30px] object-fill"
                />
              );
            })}
          </div>

          {/* {companies && (
            <Typography component="body-small" className="text-gray-850 ">
              {companies?.map(({ company }: { company: any }) => company).join(' | ')}
            </Typography>
          )} */}
          <div className="w-full bg-[#EFEFEF] h-[1px] mt-2" />
        </div>
        <div className="flex gap-3">
          <div className="flex  items-center gap-2">
            <BriefcaseSharp className="w-7 h-7 text-gray-900" />
            {industryExp && industryExpToBeShown ? (
              <div>
                <Typography component="body-small" variant="bold" className="text-gray-850">
                  {industryExp}+ Years
                </Typography>
                <Typography component="body-tiny" variant="regular" className="text-gray-650 ">
                  Industry Experience
                </Typography>
              </div>
            ) : (
              <div>
                <Typography
                  component="body-small"
                  variant="bold"
                  className="!text-gray-850 !text-sm"
                >
                  {workExperience}+ Years
                </Typography>
                <Typography
                  component="body-tiny"
                  variant="regular"
                  className="text-gray-650 !text-xs"
                >
                  Work Experience
                </Typography>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <PeopleOutline className="w-7 h-7 text-gray-900" />
            {learnersPlaced && learnersPlacedToBeShown ? (
              <div>
                <Typography component="body-small" variant="bold" className="text-gray-900">
                  {learnersPlaced}+
                </Typography>
                <Typography component="body-tiny" variant="regular" className="text-gray-650 ">
                  Learners Placed
                </Typography>
              </div>
            ) : (
              <div>
                <Typography
                  component="body-small"
                  variant="bold"
                  className="text-gray-850 !text-sm"
                >
                  {teachingExperience}+ Years
                </Typography>
                <Typography
                  component="body-tiny"
                  variant="regular"
                  className="text-gray-650 !text-xs"
                >
                  Teaching Experience
                </Typography>
              </div>
            )}
          </div>
        </div>
        <Typography
          component="body-small"
          variant="regular"
          className="line-clamp-2 text-[#757575] !text-sm"
        >
          {description}
        </Typography>
      </div>
    </div>
  );
};

export default CardDetailsView;
